/**
 * useEventDetailModals Hook
 *
 * Manages all modal state for the EventDetailPage.
 * Extracts 8+ modal states into a single organized hook.
 */

import { useState, useCallback } from 'react';
import type { TimelineEntry } from '../types/models';

export interface SelectedRole {
  id: string;
  name: string;
}

export interface UseEventDetailModalsReturn {
  // Delete confirmation
  deleteConfirm: {
    isOpen: boolean;
    open: () => void;
    close: () => void;
  };

  // Assign crew modal
  assignCrew: {
    isOpen: boolean;
    selectedRole: SelectedRole | null;
    open: (roleId: string, roleName: string) => void;
    close: () => void;
  };

  // Add role modal
  addRole: {
    isOpen: boolean;
    open: () => void;
    close: () => void;
  };

  // Timeline entry modal
  timeline: {
    isOpen: boolean;
    editingEntry: TimelineEntry | null;
    openNew: () => void;
    openEdit: (entry: TimelineEntry) => void;
    close: () => void;
  };

  // Document upload modal
  document: {
    isOpen: boolean;
    open: () => void;
    close: () => void;
  };

  // Message compose modal
  message: {
    isOpen: boolean;
    open: () => void;
    close: () => void;
  };

  // Event availability (See Availability) modal
  availability: {
    isOpen: boolean;
    open: () => void;
    close: () => void;
  };

  // Loading states for inline actions
  actions: {
    isDeleting: boolean;
    setIsDeleting: (value: boolean) => void;
    isConfirming: boolean;
    setIsConfirming: (value: boolean) => void;
    removingAssignment: string | null;
    setRemovingAssignment: (id: string | null) => void;
    confirmingAssignment: string | null;
    setConfirmingAssignment: (id: string | null) => void;
  };

  // Error state
  error: {
    message: string | null;
    set: (message: string | null) => void;
    clear: () => void;
  };
}

export function useEventDetailModals(): UseEventDetailModalsReturn {
  // Delete confirmation modal
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Assign crew modal
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<SelectedRole | null>(null);

  // Add role modal
  const [addRoleModalOpen, setAddRoleModalOpen] = useState(false);

  // Timeline entry modal
  const [timelineModalOpen, setTimelineModalOpen] = useState(false);
  const [editingTimelineEntry, setEditingTimelineEntry] = useState<TimelineEntry | null>(null);

  // Document upload modal
  const [documentModalOpen, setDocumentModalOpen] = useState(false);

  // Message compose modal
  const [messageModalOpen, setMessageModalOpen] = useState(false);

  // Event availability modal
  const [availabilityModalOpen, setAvailabilityModalOpen] = useState(false);

  // Loading states
  const [isDeleting, setIsDeleting] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [removingAssignment, setRemovingAssignment] = useState<string | null>(null);
  const [confirmingAssignment, setConfirmingAssignment] = useState<string | null>(null);

  // Error state
  const [actionError, setActionError] = useState<string | null>(null);

  // Assign crew handlers
  const openAssignCrew = useCallback((roleId: string, roleName: string) => {
    setSelectedRole({ id: roleId, name: roleName });
    setAssignModalOpen(true);
  }, []);

  const closeAssignCrew = useCallback(() => {
    setAssignModalOpen(false);
    setSelectedRole(null);
  }, []);

  // Timeline handlers
  const openTimelineNew = useCallback(() => {
    setEditingTimelineEntry(null);
    setTimelineModalOpen(true);
  }, []);

  const openTimelineEdit = useCallback((entry: TimelineEntry) => {
    setEditingTimelineEntry(entry);
    setTimelineModalOpen(true);
  }, []);

  const closeTimeline = useCallback(() => {
    setTimelineModalOpen(false);
    setEditingTimelineEntry(null);
  }, []);

  return {
    deleteConfirm: {
      isOpen: showDeleteConfirm,
      open: () => setShowDeleteConfirm(true),
      close: () => setShowDeleteConfirm(false),
    },

    assignCrew: {
      isOpen: assignModalOpen,
      selectedRole,
      open: openAssignCrew,
      close: closeAssignCrew,
    },

    addRole: {
      isOpen: addRoleModalOpen,
      open: () => setAddRoleModalOpen(true),
      close: () => setAddRoleModalOpen(false),
    },

    timeline: {
      isOpen: timelineModalOpen,
      editingEntry: editingTimelineEntry,
      openNew: openTimelineNew,
      openEdit: openTimelineEdit,
      close: closeTimeline,
    },

    document: {
      isOpen: documentModalOpen,
      open: () => setDocumentModalOpen(true),
      close: () => setDocumentModalOpen(false),
    },

    message: {
      isOpen: messageModalOpen,
      open: () => setMessageModalOpen(true),
      close: () => setMessageModalOpen(false),
    },

    availability: {
      isOpen: availabilityModalOpen,
      open: () => setAvailabilityModalOpen(true),
      close: () => setAvailabilityModalOpen(false),
    },

    actions: {
      isDeleting,
      setIsDeleting,
      isConfirming,
      setIsConfirming,
      removingAssignment,
      setRemovingAssignment,
      confirmingAssignment,
      setConfirmingAssignment,
    },

    error: {
      message: actionError,
      set: setActionError,
      clear: () => setActionError(null),
    },
  };
}
